import React from "react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IParticipationSummary } from "@Interfaces";
import {
    getParticipationDataPhase,
    participationCardSx,
    participationValueSx,
    PARTICIPATION_BODY_TEXT,
    PARTICIPATION_FONT_FAMILY,
    PARTICIPATION_INDIGO,
    PARTICIPATION_INDIGO_TINT,
    PARTICIPATION_MUTED_TEXT,
    PARTICIPATION_TEXT_SM,
    PARTICIPATION_TEXT_XS,
} from "./participationStyles";

interface TotalNumbersProps {
    summary: IParticipationSummary;
}

const mutedSubSx = {
    fontSize: PARTICIPATION_TEXT_SM,
    color: PARTICIPATION_MUTED_TEXT,
};
const indigoPillSx = {
    fontSize: PARTICIPATION_TEXT_XS,
    fontWeight: 700,
    color: PARTICIPATION_INDIGO,
    backgroundColor: PARTICIPATION_INDIGO_TINT,
    padding: "4px 10px",
    borderRadius: "999px",
};

export const TotalNumbers: React.FC<TotalNumbersProps> = ({ summary }) => {
    const { t } = useTranslation();
    const phase = getParticipationDataPhase(summary.respondents);
    const noScans = summary.qr_scans === 0;
    // No respondents yet would divide by zero and render "NaN".
    const noAverage = noScans || summary.respondents === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={PARTICIPATION_INDIGO}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M3 17l6-6 4 4 8-8" />
                    <path d="M15 7h6v6" />
                </svg>
                <Box
                    component="span"
                    sx={{
                        fontSize: PARTICIPATION_TEXT_SM,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        fontFamily: PARTICIPATION_FONT_FAMILY,
                        color: PARTICIPATION_BODY_TEXT,
                    }}
                >
                    {t("pages.participation.widgets.totalNumbers.heading")}
                </Box>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "16px",
                }}
            >
                <Box sx={participationCardSx}>
                    <Box sx={participationValueSx(noScans)}>
                        {noScans ? "–" : summary.qr_scans}
                    </Box>
                    <Box sx={mutedSubSx}>
                        {t(
                            "pages.participation.widgets.totalNumbers.scannedQr"
                        )}
                    </Box>
                    {noScans ? (
                        <Box sx={mutedSubSx}>
                            {t(
                                "pages.participation.widgets.totalNumbers.scannedQrEmpty"
                            )}
                        </Box>
                    ) : phase === "many" ? (
                        <Box component="span" sx={indigoPillSx}>
                            {t(
                                "pages.participation.widgets.totalNumbers.votedMany",
                                {
                                    respondents: summary.respondents,
                                    pct: Math.round(
                                        (summary.respondents /
                                            summary.qr_scans) *
                                            100
                                    ),
                                }
                            )}
                        </Box>
                    ) : (
                        <Box sx={mutedSubSx}>
                            {t(
                                "pages.participation.widgets.totalNumbers.votedFew",
                                { respondents: summary.respondents }
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={participationCardSx}>
                    <Box sx={participationValueSx(noScans)}>
                        {noScans ? "–" : summary.total_answers}
                    </Box>
                    <Box sx={mutedSubSx}>
                        {t(
                            "pages.participation.widgets.totalNumbers.totalVotes"
                        )}
                    </Box>
                    <Box sx={mutedSubSx}>
                        {t(
                            "pages.participation.widgets.totalNumbers.acrossQuestions",
                            { questions: summary.questions_count }
                        )}
                    </Box>
                </Box>

                <Box sx={participationCardSx}>
                    <Box sx={participationValueSx(noAverage)}>
                        {noAverage
                            ? "–"
                            : (
                                  summary.total_answers / summary.respondents
                              ).toFixed(1)}
                    </Box>
                    <Box sx={mutedSubSx}>
                        {t(
                            "pages.participation.widgets.totalNumbers.avgAnswers"
                        )}
                    </Box>
                    <Box sx={mutedSubSx}>
                        {t(
                            "pages.participation.widgets.totalNumbers.outOfQuestions",
                            { questions: summary.questions_count }
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
